import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../../styles/Header.module.scss';
import LOGO from '../../images/LOGO3.webp';
import AVATAR from '../../images/avatar.png';
import { toggleForm } from '../../Store/user/userSlice';
import { useGetProductsQuery } from '../../Store/api/apiSlice';
import { getFavorites } from '../../Store/favorites/favoritesSlice';
import { assetUrl, avatarUrl, avatarImageFallbackUrl } from '../../utils/imageUrl';
import ProductImage from '../common/ProductImage';
import { isPlaceholderAvatar } from '../../utils/defaultAvatar';
import { getProductUrl } from '../../utils/urls';

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const { currentUser, cart } = useSelector(({ user }) => user);
  const { list: favorites } = useSelector(({ favorites }) => favorites);

  const [values, setValues] = useState({ name: 'Гість', avatar: AVATAR });
  const [isPhoneDropdownOpen, setIsPhoneDropdownOpen] = useState(false);
  const [isCompactHeader, setIsCompactHeader] = useState(false);
  const phoneDropdownRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const { data = [], isLoading } = useGetProductsQuery(
    { search: searchValue },
    { skip: !searchValue || searchValue.length < 2 }
  );

  useEffect(() => {
    if (!currentUser) {
      setValues({ name: 'Гість', avatar: AVATAR });
      return;
    }

    const name = currentUser.name || 'Гість';
    if (!currentUser.avatar || isPlaceholderAvatar(currentUser.avatar)) {
      setValues({ name, avatar: AVATAR });
      return;
    }

    const primary = avatarUrl(currentUser.avatar);
    const fallback = avatarImageFallbackUrl(currentUser.avatar);
    if (!primary) {
      setValues({ name, avatar: AVATAR });
      return;
    }

    let cancelled = false;
    const loadAvatar = (url, nextUrl) => {
      const probe = new Image();
      probe.onload = () => {
        if (!cancelled) setValues({ name, avatar: url });
      };
      probe.onerror = () => {
        if (nextUrl) loadAvatar(nextUrl, null);
        else if (!cancelled) setValues({ name, avatar: AVATAR });
      };
      probe.src = url;
    };

    loadAvatar(primary, fallback || null);

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      dispatch(getFavorites());
    }
  }, [dispatch, currentUser]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (phoneDropdownRef.current && !phoneDropdownRef.current.contains(event.target)) {
        setIsPhoneDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  useEffect(() => () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 640px)');

    const updateCompact = () => setIsCompactHeader(media.matches);

    updateCompact();
    media.addEventListener('change', updateCompact);
    return () => media.removeEventListener('change', updateCompact);
  }, []);

  const handleClick = () => {
    if (!currentUser) {
      dispatch(toggleForm(true));
    } else {
      navigate('/profile');
    }
  };

  const handleSearch = ({ target: { value } }) => {
    setSearchValue(value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
    }
  };

  const openPhoneDropdown = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsPhoneDropdownOpen(true);
  };

  const handlePhoneDropdownEnter = () => {
    if (window.matchMedia('(hover: hover)').matches) {
      openPhoneDropdown();
    }
  };

  const handlePhoneDropdownLeave = () => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    hideTimeoutRef.current = setTimeout(() => {
      setIsPhoneDropdownOpen(false);
    }, 200);
  };

  const togglePhoneDropdown = () => {
    setIsPhoneDropdownOpen((prev) => !prev);
  };

  return (
    <header className={styles.header}>
      <div className={styles.logo}>
        <Link to="/">
          <img src={LOGO} alt="logo" width={160} height={48} decoding="async" />
        </Link>
      </div>

      <div className={styles.headerMain}>
        <div className={styles.topBar}>
          <button type="button" className={styles.user} onClick={handleClick}>
            <div
              className={styles.avatar}
              style={{ backgroundImage: `url(${values.avatar})` }}
            />
            <span className={styles.username}>{values.name}</span>
          </button>

          <div className={styles.topBarActions}>
            <div
              className={styles.phoneDropdown}
              ref={phoneDropdownRef}
              onMouseEnter={handlePhoneDropdownEnter}
              onMouseLeave={handlePhoneDropdownLeave}
            >
              <button
                type="button"
                className={styles.dropbtn}
                onClick={togglePhoneDropdown}
                aria-expanded={isPhoneDropdownOpen}
                aria-haspopup="true"
                aria-label="Контактні телефони"
              >
                <svg className={styles.phoneIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span className={styles.dropbtnText}>Контактний телефон</span>
                <svg
                  className={`${styles.arrowIcon} ${isPhoneDropdownOpen ? styles.arrowOpen : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {isPhoneDropdownOpen && (
                <div className={styles.phoneDropdownContent}>
                  <a href="tel:+380994902539" className={styles.phoneLink}>
                    <svg className={styles.phoneLinkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <span>+38 (099) 490-25-39</span>
                  </a>
                  <a href="tel:+380975831004" className={styles.phoneLink}>
                    <svg className={styles.phoneLinkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <span>+38 (097) 583-10-04</span>
                  </a>
                </div>
              )}
            </div>

            <div className={styles.account}>
              {currentUser && currentUser.role === 'ADMIN' && (
                <Link to="/admin" className={styles.adminLink}>
                  <span className={styles.adminLinkFull}>Адмін</span>
                  <span className={styles.adminLinkShort} aria-hidden="true">A</span>
                </Link>
              )}
              <Link to="/favorites" className={styles.favourites} aria-label="Обране">
                <svg className={`${styles['icon-fav']} ${favorites.length ? styles['icon-fav-filled'] : ''}`}>
                  <use xlinkHref={`${process.env.PUBLIC_URL}/sprite.svg#heart`} />
                </svg>
                {!!favorites.length && (
                  <span className={styles.count}>{favorites.length}</span>
                )}
              </Link>
              <Link to="/cart" className={styles.cart} aria-label="Кошик">
                <svg className={`${styles['icon-cart']} ${cart.length ? styles['icon-cart-filled'] : ''}`}>
                  <use xlinkHref={`${process.env.PUBLIC_URL}/sprite.svg#bag`} />
                </svg>
                {!!cart.length && (
                  <span className={styles.count}>{cart.length}</span>
                )}
              </Link>
            </div>
          </div>
        </div>

        <form className={styles.form} onSubmit={handleSearchSubmit}>
          <div className={styles.icon}>
            <svg className="icon" aria-hidden="true">
              <use xlinkHref={`${process.env.PUBLIC_URL}/sprite.svg#search`} />
            </svg>
          </div>
          <div className={styles.input}>
            <input
              type="search"
              name="search"
              placeholder={isCompactHeader ? 'Пошук...' : 'Введіть запит для пошуку...'}
              autoComplete="off"
              onChange={handleSearch}
              value={searchValue}
            />
          </div>
          <button type="submit" className={styles.searchSubmit} aria-label="Знайти">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-4-4" strokeLinecap="round" />
            </svg>
          </button>
          {searchValue && (
            <div className={styles.box}>
              {isLoading
                ? 'Завантаження...'
                : !data || !data.length
                  ? 'Немає результату'
                  : data.map(({ title, images, id, slug }) => (
                    <Link
                      key={id}
                      onClick={() => setSearchValue('')}
                      className={styles.item}
                      to={getProductUrl({ id, slug })}
                    >
                      <div className={styles.image}>
                        <ProductImage src={assetUrl(images[0])} alt={title} width={44} height={44} />
                      </div>
                      <div className={styles.itemTitle}>{title}</div>
                    </Link>
                  ))}
            </div>
          )}
        </form>
      </div>
    </header>
  );
};

export default Header;
